import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PasswordInput from '../components/PasswordInput';
import GradientSpinner from '../components/GradientSpinner';
import '../App.css';

const Register = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('inviteToken');
  const inviteEmail = searchParams.get('email');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'clinic',
    // Role-specific fields
    labName: '',
    licenseNumber: '',
    address: '',
    phone: '',
    clinicName: '',
    associatedLab: '',
    certificationNumber: '',
    experience: '',
    companyName: '',
    products: ''
  });
  useEffect(() => {
    if (inviteToken) setFormData((prev) => ({ ...prev, role: 'clinic' }));
    if (inviteEmail) setFormData((prev) => ({ ...prev, email: inviteEmail }));
  }, [inviteToken, inviteEmail]);

  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam && ['lab', 'clinic', 'phlebotomist', 'supplier'].includes(roleParam)) {
      setFormData((prev) => ({ ...prev, role: roleParam }));
    }
  }, [searchParams]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Prepare registration data based on role
    const registrationData = {
      email: formData.email,
      password: formData.password,
      name: formData.name,
      role: formData.role
    };

    // Add role-specific fields
    if (formData.role === 'lab') {
      registrationData.labName = formData.labName;
      registrationData.licenseNumber = formData.licenseNumber;
      registrationData.address = formData.address;
      registrationData.phone = formData.phone;
    } else if (formData.role === 'clinic') {
      registrationData.clinicName = formData.clinicName;
      if (inviteToken) registrationData.inviteToken = inviteToken;
      if (formData.associatedLab) registrationData.associatedLabs = [formData.associatedLab];
    } else if (formData.role === 'phlebotomist') {
      registrationData.certificationNumber = formData.certificationNumber;
      registrationData.experience = parseInt(formData.experience) || 0;
    } else if (formData.role === 'supplier') {
      registrationData.companyName = formData.companyName;
      registrationData.products = formData.products.split(',').map(p => p.trim()).filter(p => p);
    }

    const result = await register(registrationData);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
    }

    setLoading(false);
  };

  return (
    <div className="App" style={{ height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="container" style={{ 
        maxWidth: '600px', 
        width: '100%',
        padding: window.innerWidth <= 768 ? '16px' : '24px',
        maxHeight: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden'
      }}>
        <div style={{ marginBottom: '12px' }}>
          <Link to="/" className="back-to-home-btn">
            <span className="back-to-home-icon">←</span>
            Back to home
          </Link>
        </div>
        <div className="card auth-screen" style={{ margin: 0 }}>
          <div className="text-center mb-3">
            <img 
              src="/logo-labster.jpeg" 
              alt="THE LABSTER" 
              style={{ 
                maxWidth: window.innerWidth <= 768 ? '100px' : '120px', 
                width: '100%', 
                height: 'auto',
                marginBottom: '12px'
              }} 
            />
            <p className="page-subtitle" style={{ marginBottom: '16px' }}>Subscribe and create your account</p>
          </div>

          {error && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Name</label>
              <input
                type="text"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleChange}
                required
                style={{ padding: '8px 12px', fontSize: '0.875rem' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Email</label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                required
                style={{ padding: '8px 12px', fontSize: '0.875rem' }}
              />
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }} htmlFor="register-password">Password</label>
              <PasswordInput
                id="register-password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Role</label>
              <select
                name="role"
                className="form-select"
                value={formData.role}
                onChange={handleChange}
                required
                style={{ padding: '8px 12px', fontSize: '0.875rem' }}
              >
                <option value="clinic">Clinic</option>
                <option value="lab">Lab</option>
                <option value="phlebotomist">Phlebotomist</option>
                <option value="supplier">Supplier</option>
              </select>
            </div>

            {/* Lab-specific fields */}
            {formData.role === 'lab' && (
              <>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Lab Name</label>
                  <input
                    type="text"
                    name="labName"
                    className="form-input"
                    value={formData.labName}
                    onChange={handleChange}
                    required
                    style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>License Number</label>
                  <input
                    type="text"
                    name="licenseNumber"
                    className="form-input"
                    value={formData.licenseNumber}
                    onChange={handleChange}
                    style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Address</label>
                  <input
                    type="text"
                    name="address"
                    className="form-input"
                    value={formData.address}
                    onChange={handleChange}
                    style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    value={formData.phone}
                    onChange={handleChange}
                    style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                  />
                </div>
              </>
            )}

            {/* Clinic-specific fields */}
            {formData.role === 'clinic' && (
              <>
                {inviteToken && (
                  <div className="alert" style={{ marginBottom: '12px', background: 'rgba(51, 255, 217, 0.1)', border: '1px solid rgba(51, 255, 217, 0.3)' }}>
                    You were invited by a lab. Your clinic will be associated automatically.
                  </div>
                )}
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Clinic Name</label>
                  <input
                    type="text"
                    name="clinicName"
                    className="form-input"
                    value={formData.clinicName}
                    onChange={handleChange}
                    required
                    style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                  />
                </div>
                {!inviteToken && (
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Associated Lab ID (optional)</label>
                    <input
                      type="text"
                      name="associatedLab"
                      className="form-input"
                      value={formData.associatedLab}
                      onChange={handleChange}
                      style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                    />
                  </div>
                )}
              </>
            )}

            {/* Phlebotomist-specific fields */}
            {formData.role === 'phlebotomist' && (
              <>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Certification Number</label>
                  <input
                    type="text"
                    name="certificationNumber"
                    className="form-input"
                    value={formData.certificationNumber}
                    onChange={handleChange}
                    style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Experience (years)</label>
                  <input
                    type="number"
                    name="experience"
                    className="form-input"
                    value={formData.experience}
                    onChange={handleChange}
                    style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                  />
                </div>
              </>
            )}

            {/* Supplier-specific fields */}
            {formData.role === 'supplier' && (
              <>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    className="form-input"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label className="form-label" style={{ marginBottom: '6px', fontSize: '0.875rem' }}>Products (comma-separated)</label>
                  <input
                    type="text"
                    name="products"
                    className="form-input"
                    value={formData.products}
                    onChange={handleChange}
                    placeholder="Reagent A, Reagent B, Reagent C"
                    style={{ padding: '8px 12px', fontSize: '0.875rem' }}
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', padding: '10px', fontSize: '0.875rem', marginBottom: '8px' }}
              disabled={loading}
            >
              {
                loading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    <GradientSpinner size={20} />
                    Creating account…
                  </span>
                ) : (
                  'Create account'
                )
              }
            </button>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textAlign: 'center', margin: '0 0 12px 0', lineHeight: 1.5 }}>
              Card payments for paid plans use Stripe after sign-in — checkout is <strong style={{ color: 'var(--logo-cyan-teal)' }}>coming soon</strong> until keys are configured.
            </p>
          </form>

          <div className="text-center" style={{ marginTop: '12px' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: '#33FFD9' }}>
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;

